import React, { useState, useCallback, ChangeEvent } from 'react';
import { Box, Typography, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';

import {
  SpMatchesDefaultModal,
  SpMatchesDefaultTableList,
  SpMatchesDefaultTableRow,
  SpMatchesError,
} from 'components/SpMatchesDefaultTable';
import { SnackbarContainer, MOBILE_TOOLBAR_TOTAL } from 'components/standalone';
import { BuilderContainer } from '../containers';

interface SpMatchesSectionProps {
  canEdit: boolean;
}

export const SpMatchesSection: React.FC<SpMatchesSectionProps> = ({
  canEdit,
}) => {
  const {
    selectedBuilder,
    spBuilders,
    spBuildersLoading: loading,
    createSpBuilder,
    updateSpBuilderActive,
  } = BuilderContainer.useContainer();
  const classes = useStyles();
  const { notify } = SnackbarContainer.useContainer();
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<SpMatchesError | null>(null);

  const handleOpenModal = useCallback(() => {
    setShowModal(true);
    setError(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setError(null);
  }, []);

  const handleAddNewItem = useCallback(
    async (spName: string, active: boolean) => {
      if (!selectedBuilder) return;

      const trimmedName = spName.trim();
      if (trimmedName.length === 0) {
        setError({ message: 'Builder name cannot be empty.' });
        return;
      }

      try {
        const result = await createSpBuilder(
          selectedBuilder.id,
          trimmedName,
          active,
        );
        setError(null);
        notify('SupplyPro builder name created!', 'success');
        return result;
      } catch (err) {
        setError({ message: 'Failed to add SupplyPro builder name.' });
        notify('Failed to add a new SupplyPro builder name.', 'error');
      }
    },
    [selectedBuilder, createSpBuilder, notify],
  );

  const handleUpdateActiveFlag = useCallback(
    async (event: ChangeEvent<HTMLInputElement>, id: string) => {
      try {
        await updateSpBuilderActive(id, event.target.checked);
        notify('SupplyPro builder name updated!', 'success');
      } catch (err) {
        console.error('Error updating SP builder:', err);
        notify('Failed to update SupplyPro builder name.', 'error');
      }
    },
    [updateSpBuilderActive, notify],
  );

  const rows: SpMatchesDefaultTableRow[] | null =
    spBuilders?.map(builder => ({
      id: builder.id,
      name: builder.spName,
      active: builder.active,
    })) || null;

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography className={classes.heading} variant="h6" component="h3">
        SupplyPro - Potential Matches
      </Typography>

      <SpMatchesDefaultTableList
        columns={['SupplyPro Potential Matches']}
        rows={rows}
      />

      {canEdit && (
        <>
          <SpMatchesDefaultModal
            open={showModal}
            onClose={handleCloseModal}
            error={error}
            columns={['SupplyPro Potential Matches']}
            onUpdateActiveFlag={handleUpdateActiveFlag}
            onAddNewItem={handleAddNewItem}
            rows={rows}
          />

          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleOpenModal}
            className={classes.editButton}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: '500px',
    marginTop: '24px',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: MOBILE_TOOLBAR_TOTAL + 20,
    },
  },
  heading: {
    marginBottom: '16px',
  },
  editButton: {
    marginTop: '24px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px',
  },
}));

export default SpMatchesSection;
